import { Head, router, usePage } from "@inertiajs/react";
import { type ContextFn, format, type Locale } from "date-fns";
import {
	Calendar as CalendarIcon,
	CheckCircle,
	Loader2,
	MoreHorizontal,
	SearchIcon,
	X,
	XCircle,
} from "lucide-react";
import {
	type ChangeEvent,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import type { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import ApptPagination from "@/components/admin-portal/appointment/pagination-list";
import { PageContainer } from "@/components/admin-portal/shared/page-layout";
import { useDateContext } from "@/components/providers/date-provider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from "@/components/ui/drawer";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
	isTimePreference,
	timePreferenceShortWindow,
} from "@/lib/appointments/time-preference";
import { cn, debounce, populateQueryParams } from "@/lib/utils";
import type {
	AppointmentPendingReviewFilterOptions,
	AppointmentPendingReviewItem,
	AppointmentPendingReviewPayload,
} from "@/types/admin-portal/appointment-pending-review";
import type { GlobalPageProps as BaseGlobalPageProps } from "@/types/globals";
import type { Metadata } from "@/types/pagy";

type ReviewAction = "CONFIRMED" | "REJECTED";

type FiltersState = {
	search: string;
	status: string;
	locationCity: string;
	claimedFrom: string;
	claimedTo: string;
};

interface PendingReviewsPageProps {
	pendingReviews: AppointmentPendingReviewPayload;
	filterOptions?: AppointmentPendingReviewFilterOptions;
}

interface PendingReviewsGlobalPageProps
	extends BaseGlobalPageProps,
		PendingReviewsPageProps {
	[key: string]: any;
}

const DEFAULT_STATUS = "PENDING";

const parseDateInput = (value: string): Date | undefined => {
	if (!value) return undefined;
	const parsed = new Date(`${value}T00:00:00`);
	return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const formatDateInput = (value: Date | undefined): string | null => {
	if (!value) return null;
	const year = value.getFullYear();
	const month = String(value.getMonth() + 1).padStart(2, "0");
	const day = String(value.getDate()).padStart(2, "0");

	return `${year}-${month}-${day}`;
};

// Locale-aware date + time. Mirrors formatDateTime in Orders.tsx and the
// time-preference window: Indonesian always uses 24h; other locales follow the
// user's time format (English defaults to 12h AM/PM).
const formatDateTime = (
	value: string | null,
	locale: Locale,
	timeFormat = "hh:mm a",
	tzDate?: ContextFn<Date>,
): { date: string; time: string } => {
	if (!value) return { date: "-", time: "-" };

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return { date: value, time: "-" };

	const timeToken = locale.code === "id" ? "HH:mm" : timeFormat;

	// Render in the configured timezone (Asia/Jakarta) regardless of browser TZ.
	// Critical for the date-only `preferred_date`, whose UTC-midnight instant
	// would otherwise drift to the previous day in non-WIB browsers.
	return {
		date: format(date, "PP", { locale, in: tzDate }),
		time: format(date, timeToken, { locale, in: tzDate }),
	};
};

const pad = (value: number) => String(value).padStart(2, "0");

// Shadcn calendar for the date + a native time input — admin can custom-set
// both the date and the time-of-day when the negotiation shifts them.
function FinalTimePicker({
	value,
	onChange,
	disabled,
}: {
	value: Date | undefined;
	onChange: (date: Date | undefined) => void;
	disabled?: boolean;
}) {
	const { t } = useTranslation("appointments");
	const { locale, tzDate } = useDateContext();

	// Preserve the picked time-of-day when the user switches days.
	const handleSelectDay = (day: Date | undefined) => {
		if (!day) return;
		const next = new Date(day);
		if (value) {
			next.setHours(
				value.getHours(),
				value.getMinutes(),
				value.getSeconds(),
				0,
			);
		}
		onChange(next);
	};

	const handleTimeChange = (event: ChangeEvent<HTMLInputElement>) => {
		const [hours, minutes] = event.target.value.split(":").map(Number);
		if (Number.isNaN(hours) || Number.isNaN(minutes)) return;
		const next = value ? new Date(value) : new Date();
		next.setHours(hours, minutes, 0, 0);
		onChange(next);
	};

	const timeValue = value
		? `${pad(value.getHours())}:${pad(value.getMinutes())}`
		: "";

	return (
		<div className="flex items-center gap-2">
			<Popover>
				<PopoverTrigger asChild>
					<Button
						type="button"
						variant="outline"
						disabled={disabled}
						className={cn(
							"justify-start text-left font-normal bg-input shadow-inner",
							!value && "text-muted-foreground",
						)}
					>
						<CalendarIcon className="w-4 h-4" />
						{value
							? format(value, "PP", { locale, in: tzDate })
							: t("pending_review_mitra.drawer.fields.final_date_placeholder")}
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-auto p-0" align="start">
					<Calendar
						mode="single"
						selected={value}
						onSelect={handleSelectDay}
						initialFocus
					/>
				</PopoverContent>
			</Popover>
			<input
				type="time"
				value={timeValue}
				onChange={handleTimeChange}
				// Open the native time picker as soon as the field is clicked,
				// instead of forcing the user to hit the tiny clock indicator.
				onClick={(event) => {
					const input = event.currentTarget as HTMLInputElement & {
						showPicker?: () => void;
					};
					try {
						input.showPicker?.();
					} catch {
						// some browsers throw if the picker can't open — ignore
					}
				}}
				disabled={disabled}
				aria-label={t("pending_review_mitra.drawer.fields.final_time_label")}
				className={cn(
					"h-9 rounded-md border border-input bg-input px-3 py-1 text-sm shadow-inner ring-offset-background",
					"focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
					"disabled:cursor-not-allowed disabled:opacity-50",
				)}
			/>
		</div>
	);
}

export default function AppointmentPendingReviews({
	pendingReviews,
	filterOptions,
}: {
	pendingReviews: AppointmentPendingReviewPayload;
	filterOptions?: AppointmentPendingReviewFilterOptions;
}) {
	const { url: pageURL, props: globalProps } =
		usePage<PendingReviewsGlobalPageProps>();
	const { t } = useTranslation("appointments");
	const { queryParams } = populateQueryParams(pageURL);
	const ordersPageUrl =
		globalProps?.adminPortal?.router?.adminPortal?.appointment?.orders;
	const apptPageUrl =
		globalProps?.adminPortal?.router?.adminPortal?.appointment?.index;
	const reviewMitraClaimPathTemplate =
		globalProps?.adminPortal?.router?.adminPortal?.appointment
			?.reviewMitraClaim;

	const reviews = pendingReviews?.reviews || [];
	const meta = pendingReviews?.meta;
	const { locale: dateFnsLocale, timeFormatDateFns, tzDate } = useDateContext();

	const filters = useMemo<FiltersState>(
		() => ({
			search: queryParams?.search || "",
			status: queryParams?.status || DEFAULT_STATUS,
			locationCity: queryParams?.location_city || "",
			claimedFrom: queryParams?.claimed_from || "",
			claimedTo: queryParams?.claimed_to || "",
		}),
		[queryParams],
	);

	const hasActiveFilters = useMemo(
		() =>
			Boolean(
				filters.search ||
					filters.status !== DEFAULT_STATUS ||
					filters.locationCity ||
					filters.claimedFrom ||
					filters.claimedTo,
			),
		[filters],
	);

	const activeFilters = useMemo(
		() =>
			[
				filters.search
					? {
							key: "search",
							label: t("pending_review_mitra.filters.active.search", {
								value: filters.search,
							}),
						}
					: null,
				filters.status !== DEFAULT_STATUS
					? {
							key: "status",
							label: t("pending_review_mitra.filters.active.status", {
								value: filters.status,
							}),
						}
					: null,
				filters.locationCity
					? {
							key: "location_city",
							label: t("pending_review_mitra.filters.active.city", {
								value: filters.locationCity,
							}),
						}
					: null,
				filters.claimedFrom
					? {
							key: "claimed_from",
							label: t("pending_review_mitra.filters.active.claimed_from", {
								value: filters.claimedFrom,
							}),
						}
					: null,
				filters.claimedTo
					? {
							key: "claimed_to",
							label: t("pending_review_mitra.filters.active.claimed_to", {
								value: filters.claimedTo,
							}),
						}
					: null,
			].filter(Boolean) as Array<{ key: string; label: string }>,
		[filters, t],
	);

	const [searchInput, setSearchInput] = useState(filters.search);
	const [selectedReview, setSelectedReview] =
		useState<AppointmentPendingReviewItem | null>(null);
	const [selectedAction, setSelectedAction] = useState<ReviewAction | null>(
		null,
	);
	const [remarkInput, setRemarkInput] = useState("");
	const [finalDateTime, setFinalDateTime] = useState<Date | undefined>(
		undefined,
	);
	const [remarkError, setRemarkError] = useState<string | null>(null);
	const [isSubmittingReview, setIsSubmittingReview] = useState(false);
	const [openRemarkId, setOpenRemarkId] = useState<string | null>(null);

	const isDrawerOpen = Boolean(selectedReview && selectedAction);

	useEffect(() => {
		setSearchInput(filters.search);
	}, [filters.search]);

	const resetReviewDrawer = useCallback(() => {
		setSelectedReview(null);
		setSelectedAction(null);
		setRemarkInput("");
		setFinalDateTime(undefined);
		setRemarkError(null);
		setIsSubmittingReview(false);
	}, []);

	const openReviewDrawer = useCallback(
		(review: AppointmentPendingReviewItem, action: ReviewAction) => {
			setSelectedReview(review);
			setSelectedAction(action);
			setRemarkInput("");
			setRemarkError(null);

			if (action === "CONFIRMED") {
				// Pre-fill the final time with the mitra's proposed time
				// (fallback to any existing scheduled time).
				const initial =
					review.proposedAppointmentAt ?? review.appointmentDateTime;
				const parsed = initial ? new Date(initial) : undefined;
				setFinalDateTime(
					parsed && !Number.isNaN(parsed.getTime()) ? parsed : undefined,
				);
			} else {
				setFinalDateTime(undefined);
			}
		},
		[],
	);

	const onDrawerOpenChange = useCallback(
		(nextOpen: boolean) => {
			if (isSubmittingReview) return;
			if (!nextOpen) {
				resetReviewDrawer();
			}
		},
		[isSubmittingReview, resetReviewDrawer],
	);

	const { currentPage, limit, lastPage, paginationMetadata } = useMemo(() => {
		const totalValue = meta?.total || 0;
		const currentPageValue = meta?.page || 1;
		const limitValue = meta?.limit || 10;
		const totalPagesValue = Math.max(1, Math.ceil(totalValue / limitValue));
		const lastPageValue = totalPagesValue;
		const fromValue =
			totalValue === 0 ? 0 : (currentPageValue - 1) * limitValue + 1;
		const toValue = Math.min(currentPageValue * limitValue, totalValue);
		const buildPageUrl = (page: number, pageLimit = limitValue) => {
			const safePage = Math.max(1, Math.min(page, lastPageValue));
			const { fullUrl } = populateQueryParams(pageURL, {
				page: safePage,
				limit: pageLimit,
			});

			return fullUrl;
		};
		const paginationMetadataValue = {
			firstUrl: buildPageUrl(1),
			prevUrl: buildPageUrl(currentPageValue - 1),
			pageUrl: pageURL,
			nextUrl: buildPageUrl(currentPageValue + 1),
			lastUrl: buildPageUrl(lastPageValue),
			count: totalValue,
			page: currentPageValue,
			limit: limitValue,
			pages: totalPagesValue,
			last: lastPageValue,
			in: reviews.length,
			from: fromValue,
			to: toValue,
		} as Metadata;

		return {
			currentPage: currentPageValue,
			limit: limitValue,
			lastPage: lastPageValue,
			paginationMetadata: paginationMetadataValue,
		};
	}, [meta, pageURL, reviews.length]);

	const buildPageUrl = (page: number, pageLimit = limit) => {
		const safePage = Math.max(1, Math.min(page, lastPage));
		const { fullUrl } = populateQueryParams(pageURL, {
			page: safePage,
			limit: pageLimit,
		});

		return fullUrl;
	};

	const goToPage = (page: number, pageLimit = limit) => {
		const fullUrl = buildPageUrl(page, pageLimit);

		router.get(
			fullUrl,
			{},
			{
				preserveScroll: true,
				preserveState: true,
				replace: true,
				only: ["adminPortal", "flash", "errors", "pendingReviews"],
			},
		);
	};

	const applyFilters = useCallback(
		(nextFilters: Record<string, string | null>) => {
			const { fullUrl } = populateQueryParams(pageURL, {
				...nextFilters,
				page: 1,
			});

			router.get(
				fullUrl,
				{},
				{
					preserveScroll: true,
					preserveState: true,
					replace: true,
					only: ["adminPortal", "flash", "errors", "pendingReviews"],
				},
			);
		},
		[pageURL],
	);

	const updateFilter = useCallback(
		(
			key:
				| "search"
				| "status"
				| "location_city"
				| "claimed_from"
				| "claimed_to",
			value: string | null,
		) => {
			applyFilters({ [key]: value });
		},
		[applyFilters],
	);

	const removeFilter = useCallback(
		(
			key:
				| "search"
				| "status"
				| "location_city"
				| "claimed_from"
				| "claimed_to",
		) => {
			updateFilter(key, null);
		},
		[updateFilter],
	);

	const clearAllFilters = () => {
		applyFilters({
			search: null,
			status: null,
			location_city: null,
			claimed_from: null,
			claimed_to: null,
		});
	};

	const applySearchFilter = useMemo(
		() =>
			debounce((value: string) => updateFilter("search", value || null), 300),
		[updateFilter],
	);

	const claimedDateRange = useMemo<DateRange | undefined>(() => {
		const from = parseDateInput(filters.claimedFrom);
		const to = parseDateInput(filters.claimedTo);
		if (!from && !to) return undefined;

		return {
			from,
			to,
		};
	}, [filters.claimedFrom, filters.claimedTo]);

	const onChangeClaimedDateRange = (value: DateRange | undefined) => {
		const claimedFrom = formatDateInput(value?.from);
		const claimedTo = formatDateInput(value?.to);

		applyFilters({
			claimed_from: claimedFrom,
			claimed_to: claimedTo,
		});
	};

	const submitReviewAction = useCallback(() => {
		if (!selectedReview || !selectedAction || !reviewMitraClaimPathTemplate)
			return;

		if (selectedAction === "REJECTED" && !remarkInput.trim()) {
			setRemarkError(
				t("pending_review_mitra.drawer.validation.remark_required"),
			);
			return;
		}

		setRemarkError(null);
		const endpoint = reviewMitraClaimPathTemplate.replace(
			":id",
			selectedReview.appointmentId,
		);
		const payload: Record<string, string | null> = {
			review_action: selectedAction,
			remark: remarkInput.trim() || null,
		};
		if (selectedAction === "CONFIRMED") {
			// Final negotiated time (RFC3339); server stores UTC. Empty lets the
			// backend fall back to the mitra's proposed time.
			payload.appointment_date_time = finalDateTime
				? finalDateTime.toISOString()
				: null;
		}

		router.post(endpoint, payload, {
			preserveScroll: true,
			preserveState: true,
			onStart: () => setIsSubmittingReview(true),
			onSuccess: () => {
				toast.success(t("pending_review_mitra.drawer.toast.success"));
				resetReviewDrawer();
			},
			onFinish: () => setIsSubmittingReview(false),
		});
	}, [
		remarkInput,
		reviewMitraClaimPathTemplate,
		selectedAction,
		selectedReview,
		finalDateTime,
		t,
		resetReviewDrawer,
	]);

	return (
		<>
			<Head title={t("pending_review_mitra.head_title")} />

			<PageContainer className="min-h-[100vh] flex-1 md:min-h-min space-y-4">
				<div className="flex flex-col justify-between gap-4 md:flex-row">
					<div>
						<h1 className="text-lg font-bold tracking-tight uppercase">
							{t("pending_review_mitra.page_title")}
						</h1>
						<p className="w-full text-sm text-muted-foreground text-pretty md:max-w-[60ch]">
							{t("pending_review_mitra.page_description")}
						</p>
					</div>
				</div>

				<Separator className="bg-border" />

				<div className="space-y-3">
					<div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-5">
						<div className="relative lg:col-span-2">
							<SearchIcon className="z-10 absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground/75" />
							<Input
								type="text"
								placeholder={t(
									"pending_review_mitra.filters.search_placeholder",
								)}
								className="bg-input pl-8 shadow-inner"
								value={searchInput}
								onChange={(event) => {
									const nextValue = event.target.value;
									setSearchInput(nextValue);
									applySearchFilter(nextValue);
								}}
							/>
						</div>

						<Select
							value={filters.status}
							onValueChange={(value) => updateFilter("status", value)}
						>
							<SelectTrigger className="bg-input shadow-inner">
								<SelectValue
									placeholder={t("pending_review_mitra.filters.all_status")}
								/>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">
									{t("pending_review_mitra.filters.all_status")}
								</SelectItem>
								{(filterOptions?.statuses || []).map((status) => (
									<SelectItem key={status} value={status}>
										{status}
									</SelectItem>
								))}
							</SelectContent>
						</Select>

						<Select
							value={filters.locationCity}
							onValueChange={(value) =>
								updateFilter("location_city", value === "all" ? null : value)
							}
						>
							<SelectTrigger className="bg-input shadow-inner">
								<SelectValue
									placeholder={t("pending_review_mitra.filters.all_cities")}
								/>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">
									{t("pending_review_mitra.filters.all_cities")}
								</SelectItem>
								{(filterOptions?.locationCities || []).map((city) => (
									<SelectItem key={city} value={city}>
										{city}
									</SelectItem>
								))}
							</SelectContent>
						</Select>

						<Popover>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									className="text-left justify-start px-3 bg-input shadow-inner truncate line-clamp-1 leading-none"
								>
									{claimedDateRange?.from ? (
										claimedDateRange.to ? (
											<span>
												{filters.claimedFrom} - {filters.claimedTo}
											</span>
										) : (
											<span>{filters.claimedFrom}</span>
										)
									) : (
										<span className="font-normal text-muted-foreground">
											{t("pending_review_mitra.filters.claimed_date_range")}
										</span>
									)}
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-auto p-0" align="start">
								<Calendar
									mode="range"
									selected={claimedDateRange}
									onSelect={onChangeClaimedDateRange}
									numberOfMonths={2}
								/>
							</PopoverContent>
						</Popover>
					</div>

					{activeFilters.length > 0 && (
						<div className="flex flex-wrap items-center gap-2">
							{activeFilters.map((filter) => (
								<Badge
									key={filter.key}
									variant="secondary"
									className="rounded-full uppercase gap-1 pr-1"
								>
									{filter.label}
									<button
										type="button"
										onClick={() =>
											removeFilter(
												filter.key as
													| "search"
													| "status"
													| "location_city"
													| "claimed_from"
													| "claimed_to",
											)
										}
										className="inline-flex items-center justify-center rounded-full p-0.5 hover:bg-muted-foreground/15"
										aria-label={t("pending_review_mitra.filters.remove", {
											label: filter.label,
										})}
									>
										<X className="h-3 w-3" />
									</button>
								</Badge>
							))}

							{hasActiveFilters && (
								<Button
									variant="destructive-outline"
									size="sm"
									className="rounded-full"
									onClick={clearAllFilters}
								>
									{t("pending_review_mitra.filters.reset")}
								</Button>
							)}
						</div>
					)}
				</div>

				<div className="space-y-4 grid grid-cols-1">
					<div className="rounded-md border">
						<Table className="w-full">
							<TableHeader>
								<TableRow>
									<TableHead>
										{t("pending_review_mitra.table.reg_no")}
									</TableHead>
									<TableHead>
										{t("pending_review_mitra.table.visit_no")}
									</TableHead>
									<TableHead>
										{t("pending_review_mitra.table.preference_col")}
									</TableHead>
									<TableHead>
										{t("pending_review_mitra.table.proposal_col")}
									</TableHead>
									{/* <TableHead>{t("pending_review_mitra.table.brand")}</TableHead> */}
									<TableHead>
										{t("pending_review_mitra.table.patient")}
									</TableHead>
									<TableHead>
										{t("pending_review_mitra.table.claimed_by")}
									</TableHead>
									<TableHead>
										{t("pending_review_mitra.table.claimed_at")}
									</TableHead>
									<TableHead>
										{t("pending_review_mitra.table.reviewed_by")}
									</TableHead>
									<TableHead>
										{t("pending_review_mitra.table.status")}
									</TableHead>
									<TableHead className="text-right" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{reviews.length > 0 ? (
									reviews.map((review) => {
										const { fullUrl: detailUrl } = populateQueryParams(
											review.orderId ? ordersPageUrl : apptPageUrl,
											review.orderId
												? {
														search: review.registrationNumber,
													}
												: {
														registration_number: review.registrationNumber,
													},
										);

										return (
											<TableRow key={review.id}>
												<TableCell className="font-mono font-semibold text-nowrap">
													<a
														href={detailUrl}
														target="_blank"
														className="text-primary hover:underline hover:text-primary/80"
													>
														#{review.registrationNumber}
													</a>
												</TableCell>
												<TableCell className="text-nowrap">
													{t("pending_review_mitra.table.visit")}{" "}
													{review.visitNumber}/{review.package.visitNumber}
												</TableCell>
												<TableCell className="text-nowrap">
													{(() => {
														if (review.appointmentDateTime) {
															const f = formatDateTime(
																review.appointmentDateTime,
																dateFnsLocale,
																timeFormatDateFns,
																tzDate,
															);
															return (
																<div>
																	<p className="font-medium">{f.date}</p>
																	<p className="text-muted-foreground">
																		{f.time}
																	</p>
																</div>
															);
														}
														if (
															!review.preferredDate &&
															!isTimePreference(review.timePreference)
														) {
															return (
																<span className="text-muted-foreground">
																	{t("pending_review_mitra.table.na")}
																</span>
															);
														}
														return (
															<div className="space-y-0.5">
																<p className="font-medium">
																	{review.preferredDate
																		? formatDateTime(
																				review.preferredDate,
																				dateFnsLocale,
																				timeFormatDateFns,
																				tzDate,
																			).date
																		: t("pending_review_mitra.table.na")}
																</p>
																{isTimePreference(review.timePreference) ? (
																	<p className="text-muted-foreground italic">
																		{timePreferenceShortWindow(
																			review.timePreference,
																			t,
																			{
																				locale: dateFnsLocale,
																				timeFormat: timeFormatDateFns,
																			},
																		)}
																	</p>
																) : null}
															</div>
														);
													})()}
												</TableCell>
												<TableCell className="text-nowrap">
													{(() => {
														if (!review.proposedAppointmentAt) {
															return (
																<span className="text-muted-foreground">—</span>
															);
														}
														const f = formatDateTime(
															review.proposedAppointmentAt,
															dateFnsLocale,
															timeFormatDateFns,
															tzDate,
														);
														return (
															<div>
																<p className="font-medium">{f.date}</p>
																<p className="text-muted-foreground">
																	{f.time}
																</p>
															</div>
														);
													})()}
												</TableCell>
												{/* <TableCell>
												<div>
													<p>{review.service.name.replaceAll("_", " ")}</p>
													<p className="text-muted-foreground">
														{review.package.name} &mdash;{" "}
														{review.package.visitNumber} {t("common.visits")}
													</p>
												</div>
											</TableCell> */}
												<TableCell>
													<div className="text-nowrap">
														<p className="uppercase font-medium">
															{review.patient.name}
														</p>
														<p className="text-muted-foreground">
															{review.locationCity}
														</p>
													</div>
												</TableCell>
												<TableCell>
													<div className="text-nowrap">
														<p className="uppercase font-medium">
															{review.claimedBy?.name || "N/A"}
														</p>
														{review.claimedBy?.locationCity ? (
															<p className="text-muted-foreground">
																{review.claimedBy.locationCity}
															</p>
														) : null}
													</div>
												</TableCell>
												<TableCell className="text-nowrap">
													{(() => {
														const formatted = formatDateTime(
															review.claimedAt,
															dateFnsLocale,
															timeFormatDateFns,
															tzDate,
														);

														return (
															<div>
																<p>{formatted.date}</p>
																<p className="text-muted-foreground">
																	{formatted.time}
																</p>
															</div>
														);
													})()}
												</TableCell>
												<TableCell>
													<div className="text-nowrap">
														<p
															className={cn(
																"uppercase",
																review?.reviewedBy?.name
																	? "font-medium"
																	: "text-muted-foreground",
															)}
														>
															{review?.reviewedBy?.name ||
																t("pending_review_mitra.table.na")}
														</p>
													</div>
												</TableCell>
												<TableCell>
													{review.remark ? (
														<Popover
															open={openRemarkId === review.id}
															onOpenChange={(open) =>
																setOpenRemarkId(open ? review.id : null)
															}
														>
															<PopoverTrigger
																onMouseEnter={() => setOpenRemarkId(review.id)}
																onMouseLeave={() => setOpenRemarkId(null)}
																className={cn(
																	"inline-flex items-center rounded-full border border-transparent px-2 py-0.5 text-[10px] font-semibold bg-amber-500 text-white hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
																	{
																		"bg-emerald-500 text-white hover:bg-emerald-600":
																			review.action === "CONFIRMED",
																		"bg-destructive text-destructive-foreground hover:bg-destructive/90":
																			review.action === "REJECTED",
																	},
																)}
															>
																{t(
																	`pending_review_mitra.statuses.${review.action.toLowerCase()}`,
																)}
															</PopoverTrigger>
															<PopoverContent
																side="top"
																align="center"
																sideOffset={4}
																className="w-auto max-w-80 p-3 text-sm border shadow-none"
																onMouseEnter={() => setOpenRemarkId(review.id)}
																onMouseLeave={() => setOpenRemarkId(null)}
															>
																<div className="space-y-1 text-muted-foreground">
																	<p className="text-xs font-semibold uppercase tracking-wider">
																		Remark
																	</p>
																	<p className="text-sm leading-normal text-muted-foreground/75">
																		{review.remark}
																	</p>
																</div>
															</PopoverContent>
														</Popover>
													) : (
														<Badge
															variant="outline"
															className={cn(
																"rounded-full text-[10px] px-2 bg-amber-500 text-white shadow-none",
																{
																	"bg-emerald-500 text-white":
																		review.action === "CONFIRMED",
																	"bg-destructive text-destructive-foreground":
																		review.action === "REJECTED",
																},
															)}
														>
															{t(
																`pending_review_mitra.statuses.${review.action.toLowerCase()}`,
															)}
														</Badge>
													)}
												</TableCell>
												<TableCell className="text-right">
													<DropdownMenu>
														<DropdownMenuTrigger asChild>
															<Button
																variant="ghost"
																size="icon"
																aria-label={t(
																	"pending_review_mitra.actions.open_menu",
																)}
															>
																<MoreHorizontal className="shrink-0 size-4" />
															</Button>
														</DropdownMenuTrigger>
														<DropdownMenuContent align="end">
															<DropdownMenuLabel>
																{t("pending_review_mitra.actions.label")}
															</DropdownMenuLabel>
															<DropdownMenuSeparator />
															<DropdownMenuItem
																disabled={review.action !== "PENDING"}
																onSelect={(event) => {
																	event.preventDefault();
																	openReviewDrawer(review, "CONFIRMED");
																}}
															>
																<CheckCircle className="text-emerald-600" />
																{t("pending_review_mitra.actions.confirm")}
															</DropdownMenuItem>
															<DropdownMenuSeparator />
															<DropdownMenuItem
																className="text-destructive focus:text-destructive"
																disabled={review.action !== "PENDING"}
																onSelect={(event) => {
																	event.preventDefault();
																	openReviewDrawer(review, "REJECTED");
																}}
															>
																<XCircle className="shrink-0 size-4" />
																{t("pending_review_mitra.actions.reject")}
															</DropdownMenuItem>
														</DropdownMenuContent>
													</DropdownMenu>
												</TableCell>
											</TableRow>
										);
									})
								) : (
									<TableRow>
										<TableCell
											colSpan={11}
											className="h-24 text-center text-muted-foreground/75"
										>
											{t("pending_review_mitra.table.empty")}
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</div>

					<ApptPagination
						metadata={paginationMetadata}
						actions={{
							goToPrevpage: () => goToPage(currentPage - 1),
							goToNextPage: () => goToPage(currentPage + 1),
							onChangeLimit: (value) => goToPage(1, Number(value)),
							goToPage: (value) => goToPage(Number(value)),
							goToFirstPage: () => goToPage(1),
							goToLastPage: () => goToPage(lastPage),
						}}
					/>
				</div>

				<Drawer open={isDrawerOpen} onOpenChange={onDrawerOpenChange}>
					<DrawerContent>
						<div className="mx-auto w-full max-w-sm">
							<DrawerHeader>
								<DrawerTitle>
									{selectedAction === "REJECTED"
										? t("pending_review_mitra.drawer.reject.title")
										: t("pending_review_mitra.drawer.confirm.title")}
								</DrawerTitle>
								<DrawerDescription>
									{selectedAction === "REJECTED"
										? t("pending_review_mitra.drawer.reject.description")
										: t("pending_review_mitra.drawer.confirm.description")}
								</DrawerDescription>
							</DrawerHeader>

							<div className="px-4 pb-6 space-y-6">
								{selectedAction === "CONFIRMED" && selectedReview ? (
									<ol className="relative ml-1.5">
										<li className="relative border-l-2 border-border pl-5 pb-4">
											<span className="absolute -left-1.5 top-1 size-3 rounded-full border-2 border-background bg-muted-foreground/40" />
											<p className="text-[11px] uppercase tracking-wide text-muted-foreground">
												{t(
													"pending_review_mitra.drawer.summary.customer_preference",
												)}
											</p>
											<p className="text-sm">
												{isTimePreference(selectedReview.timePreference)
													? timePreferenceShortWindow(
															selectedReview.timePreference,
															t,
															{
																locale: dateFnsLocale,
																timeFormat: timeFormatDateFns,
															},
														)
													: t("pending_review_mitra.drawer.summary.none")}
												{selectedReview.preferredDate ? (
													<span className="text-muted-foreground">
														{" · "}
														{
															formatDateTime(
																selectedReview.preferredDate,
																dateFnsLocale,
																timeFormatDateFns,
																tzDate,
															).date
														}
													</span>
												) : null}
											</p>
										</li>
										<li className="relative border-l-2 border-border pl-5 pb-4">
											<span className="absolute -left-1.5 top-1 size-3 rounded-full border-2 border-background bg-muted-foreground/40" />
											<p className="text-[11px] uppercase tracking-wide text-muted-foreground">
												{t(
													"pending_review_mitra.drawer.summary.mitra_proposal",
												)}
											</p>
											<p className="text-sm font-medium">
												{selectedReview.proposedAppointmentAt ? (
													<>
														{
															formatDateTime(
																selectedReview.proposedAppointmentAt,
																dateFnsLocale,
																timeFormatDateFns,
																tzDate,
															).time
														}
														<span className="font-normal text-muted-foreground">
															{" · "}
															{
																formatDateTime(
																	selectedReview.proposedAppointmentAt,
																	dateFnsLocale,
																	timeFormatDateFns,
																	tzDate,
																).date
															}
														</span>
													</>
												) : (
													t("pending_review_mitra.drawer.summary.none")
												)}
											</p>
										</li>
										<li className="relative pl-5">
											<span className="absolute -left-[7px] top-1 size-3.5 rounded-full border-2 border-background bg-emerald-500" />
											<p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">
												{t(
													"pending_review_mitra.drawer.fields.final_time_label",
												)}
											</p>
											<div className="mt-1.5 space-y-1.5">
												<FinalTimePicker
													value={finalDateTime}
													onChange={setFinalDateTime}
													disabled={isSubmittingReview}
												/>
												<p className="text-xs text-muted-foreground">
													{t(
														"pending_review_mitra.drawer.fields.final_time_hint",
													)}
												</p>
											</div>
										</li>
									</ol>
								) : null}

								<div className="space-y-2">
									<Label htmlFor="review-remark">
										{t("pending_review_mitra.drawer.fields.remark_label")}{" "}
										{selectedAction === "CONFIRMED" ? (
											<span className="text-sm italic font-light">
												&mdash; (Optional)
											</span>
										) : null}
									</Label>
									<Textarea
										id="review-remark"
										value={remarkInput}
										className="bg-input"
										onChange={(event) => {
											setRemarkInput(event.target.value);
											if (remarkError) setRemarkError(null);
										}}
										placeholder={t(
											"pending_review_mitra.drawer.fields.remark_placeholder",
										)}
										rows={4}
										disabled={isSubmittingReview}
									/>

									{remarkError ? (
										<p className="text-sm text-destructive">{remarkError}</p>
									) : null}
								</div>
							</div>

							<DrawerFooter className="gap-4 md:gap-2 md:flex-row md:justify-end">
								<DrawerClose asChild>
									<Button
										type="button"
										variant="ghost"
										disabled={isSubmittingReview}
									>
										{t("pending_review_mitra.drawer.buttons.cancel")}
									</Button>
								</DrawerClose>
								<Button
									type="button"
									onClick={submitReviewAction}
									disabled={isSubmittingReview}
									variant={
										selectedAction === "REJECTED" ? "destructive" : "default"
									}
								>
									{isSubmittingReview ? (
										<>
											<Loader2 className="size-4 animate-spin" />
											{t("pending_review_mitra.drawer.buttons.submitting")}
										</>
									) : selectedAction === "REJECTED" ? (
										t("pending_review_mitra.drawer.buttons.reject")
									) : (
										t("pending_review_mitra.drawer.buttons.confirm")
									)}
								</Button>
							</DrawerFooter>
						</div>
					</DrawerContent>
				</Drawer>
			</PageContainer>
		</>
	);
}
